import { Stack } from "@mui/material";
import type { GridAutoGeneratedGroupNode, GridDataGroupNode, GridLeafNode } from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse } from "@entrilia/shared/api/types";
import { combineComparers, stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../api/adminApi";
import type { Category } from "../../../../../api/types/accessTypes";
import type {
  Contact,
  ContactWithFieldValues,
  FundPermissionsResponse,
  InvestorContact,
  UpdateInvestorCommunicationMatrixRequest,
} from "../../../../../api/types/fundStructureTypes";
import AddContactDialog from "../../common/AddContactDialog";
import CommunicationMatrix from "../../common/CommunicationMatrix";
import { FundMatrixContactRowCell, FundMatrixInvestorGroupCell } from "../../common/matrixGridComponents";
import type { CommunicationMatrixModel } from "../../common/matrixHelper";
import {
  cloneFundModelWithoutChildren,
  createFundModels,
  createFundPermissionModelsAddedContacts,
  getContactChangesWhereInvitationWouldBeSent,
  getFundMatrixTreeDataPath,
  getGroupNodeModel,
  getModelId,
} from "../../common/matrixHelper";
import AssignInvestorContactDialog from "../../investors/communication-matrix/AssignInvestorContactDialog";

interface Props {
  permissionsResponse: FundPermissionsResponse;
  categories: Category[];
  contacts: ContactWithFieldValues[];
}

const sortModelFunc = combineComparers<CommunicationMatrixModel>(
  stringComparerBy((m) => m.investorName),
  stringComparerBy((m) => m.contactName)
);

const updateMatrixFundCommunicationMatrix = withErrorHandling(adminApi.updateFundCommunicationMatrix);

interface DialogState {
  openDialog?: "assign_investor_contact" | "create_contact";
  selectedInvestorModel?: CommunicationMatrixModel;
}

const FundCommunicationMatrix = ({ permissionsResponse, categories, contacts }: Props) => {
  const { fundId, fundName, permissions } = permissionsResponse;
  const originalModels: CommunicationMatrixModel[] = useMemo(
    () => createFundModels(permissions, fundId, fundName),
    [fundId, fundName, permissions]
  );

  const [initialModels, setInitialModels] = useState<CommunicationMatrixModel[]>(() => structuredClone(originalModels));
  const [models, setModels] = useState<CommunicationMatrixModel[]>(() => structuredClone(originalModels));
  const [contactsList, setContactsList] = useState<Contact[]>([...contacts]);

  const [dialogState, setDialogState] = useState<DialogState>({});

  const contactsForOptions = useMemo(() => {
    return contactsList
      .filter(
        (contact) =>
          !models.some(
            (model) =>
              model.investorName === dialogState.selectedInvestorModel?.investorName && model.contactId === contact.id
          ) //exclude already added
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [contactsList, models, dialogState.selectedInvestorModel?.investorName]);

  const { existingContactNames, existingContactEmails } = useMemo(() => {
    const existingContactNames = new Set<string>(contacts.map((c) => c.name));
    const existingContactEmails = new Set<string>(contacts.map((c) => c.email.toLowerCase()));
    return { existingContactNames, existingContactEmails };
  }, [contacts]);

  const getChangesWhereInvitationWouldBeSent = (
    initialModels: CommunicationMatrixModel[],
    modifiedModels: CommunicationMatrixModel[]
  ) => getContactChangesWhereInvitationWouldBeSent(contacts, initialModels, modifiedModels);

  const handleContactAdd = (node: GridDataGroupNode | GridAutoGeneratedGroupNode | GridLeafNode) => {
    const model =
      node.type === "group"
        ? models.find((m) => (node.children as string[]).includes(getModelId(m)))
        : models.find((m) => m.investorName === node.groupingKey);

    if (model) {
      setDialogState({ openDialog: "assign_investor_contact", selectedInvestorModel: model });
    }
  };

  const createModelsForAddedContacts = (contacts: InvestorContact[]) => {
    setModels((currentModels) =>
      dialogState.selectedInvestorModel
        ? [...currentModels, ...createFundPermissionModelsAddedContacts(dialogState.selectedInvestorModel, contacts)]
        : currentModels
    );
  };

  const updateCommunicationMatrix = async (
    payload: UpdateInvestorCommunicationMatrixRequest
  ): Promise<ApiResponse<void | CommunicationMatrixModel[]>> => {
    const [updatedPermissions, error] = await updateMatrixFundCommunicationMatrix(fundId, payload);
    return {
      data: updatedPermissions && createFundModels(updatedPermissions.permissions, fundId, fundName),
      error,
      success: !error,
    };
  };

  const handleContactsSelected = (selectedContacts: Contact[]) => {
    const investorContacts: InvestorContact[] = selectedContacts.map((contact) => ({
      contactId: contact.id,
      name: contact.name,
      email: contact.email,
    }));

    createModelsForAddedContacts(investorContacts);
    setDialogState({});
  };

  const handleContactCreated = (newContact: Contact) => {
    createModelsForAddedContacts([{ contactId: newContact.id, name: newContact.name, email: newContact.email }]);
    setDialogState({});
    setContactsList((prev) => [...prev, newContact]);
  };

  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      <CommunicationMatrix
        entityType="Fund"
        noItemsMessage={"This fund has not been added to any investor."}
        renderGroupCellElement={(value, node) => (
          <FundMatrixInvestorGroupCell
            value={value as string}
            groupingKey={node.groupingKey ? (node.groupingKey as string) : undefined}
            investorId={getGroupNodeModel(models, node)?.investorId}
            onContactAdd={() => handleContactAdd(node)}
          />
        )}
        categories={categories}
        initialModels={initialModels}
        setInitialModels={setInitialModels}
        models={models}
        setModels={setModels}
        getChangesWhereInvitationWouldBeSent={getChangesWhereInvitationWouldBeSent}
        sortModelFunc={sortModelFunc}
        getTreeDataPath={getFundMatrixTreeDataPath}
        cloneModelWithoutChildren={cloneFundModelWithoutChildren}
        renderGroupedRowCell={(model: CommunicationMatrixModel) => <FundMatrixContactRowCell model={model} />}
        updateInvestorCommunicationMatrix={updateCommunicationMatrix}
        groupRowActionsDisabled
      />
      <AssignInvestorContactDialog
        open={dialogState.openDialog === "assign_investor_contact" && dialogState.selectedInvestorModel !== undefined}
        onClose={() => setDialogState({})}
        title={`Add contact(s) to ${dialogState.selectedInvestorModel?.investorName}`}
        description="Select the contacts you wish to associate with this fund."
        onCreateNew={() =>
          setDialogState((prev) => ({
            openDialog: "create_contact",
            selectedInvestorModel: prev.selectedInvestorModel,
          }))
        }
        options={contactsForOptions}
        onContactsSelected={handleContactsSelected}
      />
      {dialogState.openDialog === "create_contact" && dialogState.selectedInvestorModel !== undefined && (
        <AddContactDialog
          mode="create"
          onClose={() => setDialogState({})}
          onCreate={handleContactCreated}
          existingContactNames={existingContactNames}
          existingContactEmails={existingContactEmails}
        />
      )}
    </Stack>
  );
};

export default FundCommunicationMatrix;
